/**
 * Text Selection Handler
 * 
 * Manages text selection preservation during field formatting operations.
 * Handles complex selection scenarios including partial selection formatting,
 * selection expansion, and cross-delimiter selections.
 */

import { FieldType } from '../detection-patterns';
import { PositionCalculator, CharacterMapping } from './position-calculator';

/**
 * Text selection information
 */
export interface SelectionInfo {
  /** Selection start position */
  start: number;
  /** Selection end position */
  end: number;
  /** Selected text content */
  text: string;
  /** Whether there's an active selection */
  hasSelection: boolean;
  /** Selection direction */
  direction: 'forward' | 'backward' | 'none';
  /** Whether selection spans format delimiters */
  spansDelimiters: boolean;
}

/**
 * Selection operation context
 */
export interface SelectionOperationContext {
  /** Original text before operation */
  originalText: string;
  /** New text after operation */
  newText: string;
  /** Original selection */
  originalSelection: SelectionInfo;
  /** Field type */
  fieldType: FieldType;
  /** Type of operation */
  operationType: SelectionOperationType;
  /** Whether formatting was applied */
  formatApplied: boolean;
}

/**
 * Types of selection operations
 */
export enum SelectionOperationType {
  /** Text input/replacement within selection */
  REPLACE = 'replace',
  /** Delete selected text */
  DELETE = 'delete',
  /** Format selected text */
  FORMAT = 'format',
  /** Paste over selection */
  PASTE = 'paste',
  /** Extend selection due to formatting */
  EXPAND = 'expand',
  /** Maintain selection during validation */
  PRESERVE = 'preserve'
}

/**
 * Selection preservation result
 */
export interface SelectionPreservationResult {
  /** New selection to apply */
  newSelection: SelectionInfo;
  /** Whether selection was successfully preserved */
  preserved: boolean;
  /** Confidence level (0-1) in preservation */
  confidence: number;
  /** Method used for preservation */
  method: string;
  /** Whether selection should be cleared */
  clearSelection: boolean;
  /** Explanation of preservation decision */
  reason: string;
}

/**
 * Selection validation result
 */
export interface SelectionValidationResult {
  /** Whether selection is valid for the field type */
  isValid: boolean;
  /** Adjusted selection if original is invalid */
  adjustedSelection?: SelectionInfo;
  /** Validation errors */
  errors: string[];
  /** Warnings about selection behavior */
  warnings: string[];
}

/**
 * Selection Handler Class
 * 
 * Manages text selection preservation and adjustment during formatting operations.
 * Uses character mapping and format-aware logic to maintain meaningful selections.
 */
export class SelectionHandler {
  private readonly positionCalculator: PositionCalculator;
  private readonly debugMode: boolean;

  // Selection preservation statistics
  private preservationAttempts: number = 0;
  private successfulPreservations: number = 0;

  constructor(debugMode: boolean = false) {
    this.debugMode = debugMode;
    this.positionCalculator = new PositionCalculator(debugMode);
  }

  /**
   * Get current text selection from element
   * 
   * @param element The input element
   * @returns Current selection information
   */
  public getSelection(element: HTMLInputElement | HTMLTextAreaElement): SelectionInfo {
    try {
      const start = element.selectionStart || 0;
      const end = element.selectionEnd || 0;
      const hasSelection = start !== end;
      
      let direction: 'forward' | 'backward' | 'none' = 'none';
      if (hasSelection) {
        try {
          const selectionDirection = (element as any).selectionDirection;
          direction = selectionDirection === 'backward' ? 'backward' : 'forward';
        } catch {
          direction = 'forward';
        }
      }

      const selectedText = hasSelection ? element.value.substring(
        Math.min(start, end),
        Math.max(start, end)
      ) : '';

      // Check if selection spans delimiters
      const spansDelimiters = this.checkIfSelectionSpansDelimiters(
        selectedText,
        this.getFieldTypeFromElement(element)
      );

      return {
        start: Math.min(start, end),
        end: Math.max(start, end),
        text: selectedText,
        hasSelection,
        direction,
        spansDelimiters
      };

    } catch (error) {
      this.log(`Error getting selection: ${error}`, 'warn');
      return this.getEmptySelection();
    }
  }

  /**
   * Set text selection on element
   * 
   * @param element The input element
   * @param selection Selection to apply
   * @param delay Optional delay for setting selection
   */
  public setSelection(
    element: HTMLInputElement | HTMLTextAreaElement,
    selection: SelectionInfo,
    delay: number = 0
  ): void {
    const applySelection = () => {
      try {
        // Ensure element is focused
        if (document.activeElement !== element) {
          element.focus();
        }

        // Apply selection
        if (selection.hasSelection && selection.start !== selection.end) {
          element.setSelectionRange(
            selection.start,
            selection.end,
            selection.direction || 'forward'
          );
        } else {
          // Set cursor position
          element.setSelectionRange(selection.start, selection.start);
        }

        if (this.debugMode) {
          this.log(`Selection applied: ${JSON.stringify(selection)}`);
        }

      } catch (error) {
        this.log(`Error setting selection: ${error}`, 'warn');
      }
    };

    if (delay > 0) {
      setTimeout(applySelection, delay);
    } else {
      applySelection();
    }
  }

  /**
   * Preserve text selection during formatting operation
   * 
   * @param context Selection operation context
   * @returns Selection preservation result
   */
  public preserveSelection(context: SelectionOperationContext): SelectionPreservationResult {
    this.preservationAttempts++;

    try {
      this.log(`Preserving selection for ${context.operationType}: originalSelection=${JSON.stringify(context.originalSelection)}, originalText="${context.originalText}", newText="${context.newText}"`);

      // Handle different operation types
      switch (context.operationType) {
        case SelectionOperationType.REPLACE:
          return this.handleReplaceOperation(context);
        
        case SelectionOperationType.DELETE:
          return this.handleDeleteOperation(context);
        
        case SelectionOperationType.FORMAT:
          return this.handleFormatOperation(context);
        
        case SelectionOperationType.PASTE:
          return this.handlePasteOperation(context);
        
        case SelectionOperationType.EXPAND:
          return this.handleExpandOperation(context);
        
        case SelectionOperationType.PRESERVE:
          return this.handlePreserveOperation(context);
        
        default:
          return this.getFallbackPreservation(context, 'Unknown operation type');
      }

    } catch (error) {
      this.log(`Error preserving selection: ${error}`, 'error');
      return this.getFallbackPreservation(context, `Error: ${error}`);
    }
  }

  /**
   * Validate a text selection for a field type
   * 
   * @param selection Selection to validate
   * @param text Text content
   * @param fieldType Field type
   * @returns Validation result
   */
  public validateSelection(
    selection: SelectionInfo,
    text: string,
    fieldType: FieldType
  ): SelectionValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check basic validity
    if (selection.start < 0 || selection.end > text.length || selection.start > selection.end) {
      errors.push('Selection bounds are invalid');
    }

    // Check field-specific constraints
    const fieldValidation = this.validateSelectionForField(selection, text, fieldType);
    errors.push(...fieldValidation.errors);
    warnings.push(...fieldValidation.warnings);

    // Create adjusted selection if needed
    let adjustedSelection: SelectionInfo | undefined;
    if (errors.length > 0) {
      adjustedSelection = this.adjustInvalidSelection(selection, text, fieldType);
    }

    return {
      isValid: errors.length === 0,
      adjustedSelection,
      errors,
      warnings
    };
  }

  /**
   * Expand selection to include logical formatting boundaries
   * 
   * @param selection Current selection
   * @param text Text content
   * @param fieldType Field type
   * @returns Expanded selection
   */
  public expandSelectionToFormatBoundaries(
    selection: SelectionInfo,
    text: string,
    fieldType: FieldType
  ): SelectionInfo {
    if (!selection.hasSelection) {
      return selection;
    }

    const delimiters = this.getDelimitersForFieldType(fieldType);
    let expandedStart = selection.start;
    let expandedEnd = selection.end;

    // Expand start backward to format boundary
    while (expandedStart > 0) {
      const charBefore = text[expandedStart - 1];
      if (delimiters.includes(charBefore)) {
        break; // Stop at delimiter
      }
      const charAtStart = text[expandedStart];
      if (delimiters.includes(charAtStart)) {
        expandedStart--; // Include the delimiter
        break;
      }
      expandedStart--;
    }

    // Expand end forward to format boundary
    while (expandedEnd < text.length) {
      const charAfter = text[expandedEnd];
      if (delimiters.includes(charAfter)) {
        expandedEnd++; // Include the delimiter
        break;
      }
      expandedEnd++;
    }

    return {
      ...selection,
      start: expandedStart,
      end: expandedEnd,
      text: text.substring(expandedStart, expandedEnd)
    };
  }

  /**
   * Get performance statistics
   */
  public getPerformanceStats(): {
    preservationAttempts: number;
    successfulPreservations: number;
    successRate: number;
  } {
    return {
      preservationAttempts: this.preservationAttempts,
      successfulPreservations: this.successfulPreservations,
      successRate: this.preservationAttempts > 0 ? 
        this.successfulPreservations / this.preservationAttempts : 0
    };
  }

  /**
   * Reset performance statistics
   */
  public resetPerformanceStats(): void {
    this.preservationAttempts = 0;
    this.successfulPreservations = 0;
  }

  // Private methods for handling different operation types

  /**
   * Handle replace operation (text input in selection)
   */
  private handleReplaceOperation(context: SelectionOperationContext): SelectionPreservationResult {
    // After replacement, place cursor at end of new content
    const replacementLength = context.newText.length - context.originalText.length + 
                             (context.originalSelection.end - context.originalSelection.start);
    
    const newCursorPosition = context.originalSelection.start + Math.max(0, replacementLength);

    return {
      newSelection: {
        start: newCursorPosition,
        end: newCursorPosition,
        text: '',
        hasSelection: false,
        direction: 'none',
        spansDelimiters: false
      },
      preserved: false, // Selection is cleared after replacement
      confidence: 0.9,
      method: 'Cursor positioning after replacement',
      clearSelection: true,
      reason: 'Selection replaced with new content'
    };
  }

  /**
   * Handle delete operation
   */
  private handleDeleteOperation(context: SelectionOperationContext): SelectionPreservationResult {
    // After deletion, place cursor at start of original selection
    const newCursorPosition = Math.min(
      context.originalSelection.start,
      context.newText.length
    );

    return {
      newSelection: {
        start: newCursorPosition,
        end: newCursorPosition,
        text: '',
        hasSelection: false,
        direction: 'none',
        spansDelimiters: false
      },
      preserved: false,
      confidence: 0.95,
      method: 'Cursor at selection start after deletion',
      clearSelection: true,
      reason: 'Selection deleted'
    };
  }

  /**
   * Handle format operation
   */
  private handleFormatOperation(context: SelectionOperationContext): SelectionPreservationResult {
    if (!context.formatApplied) {
      // No formatting applied, preserve original selection
      const validatedSelection = this.validateSelection(
        context.originalSelection,
        context.newText,
        context.fieldType
      );

      if (validatedSelection.isValid) {
        this.successfulPreservations++;
        return {
          newSelection: context.originalSelection,
          preserved: true,
          confidence: 0.9,
          method: 'Original selection preserved (no formatting)',
          clearSelection: false,
          reason: 'No formatting changes applied'
        };
      }
    }

    // Use character mapping to preserve selection
    return this.preserveSelectionWithMapping(context);
  }

  /**
   * Handle paste operation
   */
  private handlePasteOperation(context: SelectionOperationContext): SelectionPreservationResult {
    // Place cursor at end after paste
    return {
      newSelection: {
        start: context.newText.length,
        end: context.newText.length,
        text: '',
        hasSelection: false,
        direction: 'none',
        spansDelimiters: false
      },
      preserved: false,
      confidence: 0.95,
      method: 'Cursor at end after paste',
      clearSelection: true,
      reason: 'Paste operation completed'
    };
  }

  /**
   * Handle expand operation
   */
  private handleExpandOperation(context: SelectionOperationContext): SelectionPreservationResult {
    // Expand selection to include format boundaries
    const expandedSelection = this.expandSelectionToFormatBoundaries(
      context.originalSelection,
      context.newText,
      context.fieldType
    );

    this.successfulPreservations++;
    return {
      newSelection: expandedSelection,
      preserved: true,
      confidence: 0.8,
      method: 'Selection expanded to format boundaries',
      clearSelection: false,
      reason: 'Selection expanded for better formatting behavior'
    };
  }

  /**
   * Handle preserve operation
   */
  private handlePreserveOperation(context: SelectionOperationContext): SelectionPreservationResult {
    return this.preserveSelectionWithMapping(context);
  }

  /**
   * Preserve selection using character mapping
   */
  private preserveSelectionWithMapping(context: SelectionOperationContext): SelectionPreservationResult {
    try {
      // Create character mapping
      const mappings = this.positionCalculator.createCharacterMapping(
        context.originalText,
        context.newText,
        context.fieldType
      );

      // Map selection start and end positions
      const newStart = this.positionCalculator.mapCursorPosition(
        mappings,
        context.originalSelection.start,
        0
      );

      const newEnd = this.positionCalculator.mapCursorPosition(
        mappings,
        context.originalSelection.end,
        context.newText.length
      );

      // Create new selection
      const newSelection: SelectionInfo = {
        start: Math.min(newStart, newEnd),
        end: Math.max(newStart, newEnd),
        text: context.newText.substring(Math.min(newStart, newEnd), Math.max(newStart, newEnd)),
        hasSelection: newStart !== newEnd,
        direction: context.originalSelection.direction,
        spansDelimiters: false // Will be calculated
      };

      // Check if new selection spans delimiters
      newSelection.spansDelimiters = this.checkIfSelectionSpansDelimiters(
        newSelection.text,
        context.fieldType
      );

      // Validate new selection
      const validation = this.validateSelection(newSelection, context.newText, context.fieldType);
      
      if (validation.isValid) {
        this.successfulPreservations++;
        return {
          newSelection,
          preserved: true,
          confidence: 0.85,
          method: 'Character mapping preservation',
          clearSelection: !newSelection.hasSelection,
          reason: 'Selection mapped using character positions'
        };
      } else {
        // Use adjusted selection if available
        if (validation.adjustedSelection) {
          return {
            newSelection: validation.adjustedSelection,
            preserved: false,
            confidence: 0.6,
            method: 'Adjusted selection after mapping failure',
            clearSelection: !validation.adjustedSelection.hasSelection,
            reason: 'Original mapping failed, using adjusted selection'
          };
        }
      }

    } catch (error) {
      this.log(`Character mapping preservation failed: ${error}`, 'warn');
    }

    return this.getFallbackPreservation(context, 'Character mapping failed');
  }

  /**
   * Get fallback selection preservation
   */
  private getFallbackPreservation(
    context: SelectionOperationContext,
    reason: string
  ): SelectionPreservationResult {
    return {
      newSelection: {
        start: context.newText.length,
        end: context.newText.length,
        text: '',
        hasSelection: false,
        direction: 'none',
        spansDelimiters: false
      },
      preserved: false,
      confidence: 0.1,
      method: 'Fallback to end position',
      clearSelection: true,
      reason
    };
  }

  // Utility methods

  /**
   * Get empty selection
   */
  private getEmptySelection(): SelectionInfo {
    return {
      start: 0,
      end: 0,
      text: '',
      hasSelection: false,
      direction: 'none',
      spansDelimiters: false
    };
  }

  /**
   * Check if selection spans delimiters
   */
  private checkIfSelectionSpansDelimiters(selectedText: string, fieldType: FieldType): boolean {
    const delimiters = this.getDelimitersForFieldType(fieldType);
    return delimiters.some(delimiter => selectedText.includes(delimiter));
  }

  /**
   * Get delimiters for field type
   */
  private getDelimitersForFieldType(fieldType: FieldType): string[] {
    const delimiterMap: Record<FieldType, string[]> = {
      [FieldType.PHONE]: ['(', ')', ' ', '-'],
      [FieldType.ZIP]: ['-'],
      [FieldType.DATE_OF_BIRTH]: ['/'],
      [FieldType.SSN]: ['-'],
      [FieldType.CURRENCY]: ['$', ',', '.'],
      [FieldType.UNKNOWN]: []
    };

    return delimiterMap[fieldType] || [];
  }

  /**
   * Get field type from element (simplified for now)
   */
  private getFieldTypeFromElement(element: HTMLElement): FieldType {
    // This would integrate with field registry in real implementation
    return FieldType.UNKNOWN;
  }

  /**
   * Validate selection for specific field type
   */
  private validateSelectionForField(
    selection: SelectionInfo,
    text: string,
    fieldType: FieldType
  ): { errors: string[], warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Field-specific validation logic would go here
    // For now, basic validation only

    if (selection.start < 0) {
      errors.push('Selection start cannot be negative');
    }

    if (selection.end > text.length) {
      errors.push('Selection end exceeds text length');
    }

    return { errors, warnings };
  }

  /**
   * Adjust invalid selection
   */
  private adjustInvalidSelection(
    selection: SelectionInfo,
    text: string,
    fieldType: FieldType
  ): SelectionInfo {
    const adjustedStart = Math.max(0, Math.min(selection.start, text.length));
    const adjustedEnd = Math.max(adjustedStart, Math.min(selection.end, text.length));

    return {
      ...selection,
      start: adjustedStart,
      end: adjustedEnd,
      text: text.substring(adjustedStart, adjustedEnd),
      hasSelection: adjustedStart !== adjustedEnd
    };
  }

  /**
   * Debug logging utility
   */
  private log(message: string, level: 'log' | 'warn' | 'error' = 'log', data?: any): void {
    if (this.debugMode) {
      const prefix = '[SelectionHandler]';
      if (data) {
        console[level](`${prefix} ${message}`, data);
      } else {
        console[level](`${prefix} ${message}`);
      }
    }
  }
}